import { Link, useParams } from 'react-router-dom';
import PageShell from '@/components/feature/PageShell';
import Seo from '@/components/base/Seo';
import Breadcrumbs from '@/components/base/Breadcrumbs';
import ArticleCard from '@/components/base/ArticleCard';
import ArticleBody from '@/pages/insight/detail/components/ArticleBody';
import RelatedArticles from '@/pages/insight/detail/components/RelatedArticles';
import { formatDate, getArticleById, getCategoryBySlug, getRelatedArticles } from '@/lib/content';

const toneBadge: Record<string, string> = {
  primary: 'bg-primary-100 text-primary-800',
  accent: 'bg-accent-100 text-accent-900',
  secondary: 'bg-secondary-100 text-secondary-900',
};

function formatViews(views: number): string {
  if (views >= 10000) return `${(views / 10000).toFixed(1)} 万`;
  return `${views}`;
}

export default function ArticleDetailPage() {
  const { id } = useParams<{ id: string }>();
  const article = id ? getArticleById(id) : undefined;

  if (!article) {
    return (
      <PageShell>
        <Seo
          title="未找到该观察条目 - 职镜观察"
          description="你访问的观察条目不存在或已调整地址，可以返回内幕观察列表继续浏览其他内容。"
        />
        <div className="mx-auto max-w-content px-4 py-24 text-center md:px-6">
          <i className="ri-file-unknow-line text-4xl text-foreground-300" />
          <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">没有找到这篇观察</h1>
          <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-foreground-600">
            该条目的地址可能已经调整。你可以返回观察列表，或按主题分类查找更接近的内容。
          </p>
          <div className="mt-7 flex flex-col justify-center gap-3 sm:flex-row">
            <Link
              to="/insight"
              className="whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 font-label text-sm text-background-50 transition-colors hover:bg-primary-600"
            >
              返回观察列表
            </Link>
            <Link
              to="/"
              className="whitespace-nowrap rounded-md border border-background-300 px-5 py-3 font-label text-sm text-foreground-800 transition-colors hover:border-primary-400"
            >
              回到首页
            </Link>
          </div>
        </div>
      </PageShell>
    );
  }

  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const category = getCategoryBySlug(article.categorySlug);
  const related = getRelatedArticles(article, 3);
  const badgeClass = category ? toneBadge[category.tone] : toneBadge.primary;

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      headline: article.title,
      description: article.excerpt,
      image: article.cover,
      datePublished: article.date,
      dateModified: article.date,
      articleSection: category?.name ?? '职场观察',
      keywords: article.tags.join(','),
      inLanguage: 'zh-CN',
      author: { '@type': 'Organization', name: article.author },
      publisher: { '@type': 'Organization', name: '职镜观察' },
      mainEntityOfPage: { '@type': 'WebPage', '@id': `${origin}/insight/${article.id}` },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: `${origin}/` },
        { '@type': 'ListItem', position: 2, name: '内幕观察', item: `${origin}/insight` },
        { '@type': 'ListItem', position: 3, name: article.title, item: `${origin}/insight/${article.id}` },
      ],
    },
  ];

  return (
    <PageShell>
      <Seo
        title={`${article.title} - 职镜观察`}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        image={article.cover}
        type="article"
        jsonLd={jsonLd}
      />

      <article>
        <header className="border-b border-background-200 bg-background-100 paper-grain">
          <div className="mx-auto max-w-content px-4 pb-10 pt-8 md:px-6 md:pb-12 md:pt-10">
            <Breadcrumbs
              items={[
                { name: '首页', path: '/' },
                { name: '内幕观察', path: '/insight' },
                ...(category ? [{ name: category.name, path: `/category/${category.slug}` }] : []),
                { name: '正文' },
              ]}
            />
            <div className="max-w-3xl">
              <div className="flex flex-wrap items-center gap-3">
                <span className={`rounded-full px-3 py-1 font-label text-xs ${badgeClass}`}>
                  {category?.name ?? '观察'}
                </span>
                <span className="font-label text-xs text-foreground-500">{formatDate(article.date)}</span>
                <span className="font-label text-xs text-foreground-500">{article.readMinutes} 分钟阅读</span>
                <span className="font-label text-xs text-foreground-500">
                  {formatViews(article.views)} 次阅读
                </span>
              </div>
              <h1 className="mt-4 font-heading text-2xl font-black leading-snug text-foreground-950 md:text-4xl md:leading-[1.3]">
                {article.title}
              </h1>
              <p className="mt-4 text-sm leading-relaxed text-foreground-700 md:text-base">{article.excerpt}</p>
              <div className="mt-5 flex flex-wrap items-center gap-2">
                {article.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/insight?q=${encodeURIComponent(tag)}`}
                    className="rounded-full border border-background-300 px-3 py-1 font-label text-xs text-foreground-600 transition-colors hover:border-primary-400 hover:text-primary-700"
                  >
                    #{tag}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </header>

        <div className="mx-auto max-w-content px-4 py-10 md:px-6 md:py-14">
          <figure className="mb-10 overflow-hidden rounded-lg">
            <img
              src={article.cover}
              alt={article.title}
              title={`${article.title} - ${category?.name ?? '职场观察'}`}
              className="h-56 w-full object-cover object-top md:h-[420px]"
            />
            <figcaption className="mt-3 font-label text-xs text-foreground-500">
              配图仅用于主题示意，与文中涉及的具体企业无直接对应关系。
            </figcaption>
          </figure>

          <div className="grid gap-10 lg:grid-cols-12 lg:gap-12">
            <div className="lg:col-span-8">
              <div className="mb-8 flex items-center gap-3 border-b border-background-200 pb-6">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-secondary-100 font-heading text-sm font-bold text-secondary-800">
                  {article.author.slice(0, 1)}
                </span>
                <div>
                  <p className="font-label text-sm font-medium text-foreground-900">{article.author}</p>
                  <p className="font-label text-xs text-foreground-500">
                    整理于 {formatDate(article.date)} · 发布前经过公开信息交叉核对
                  </p>
                </div>
              </div>

              <ArticleBody sections={article.content} />

              <div className="mt-10 rounded-lg border border-accent-200 bg-accent-50 p-5 md:p-6">
                <div className="flex items-start gap-3">
                  <i className="ri-error-warning-line mt-0.5 text-lg text-accent-700" />
                  <p className="text-sm leading-relaxed text-accent-900">
                    本文基于公开资料与投稿整理，旨在提供信息参考，不构成法律意见。涉及具体劳动争议时，
                    建议结合自身合同条款与当地规定，必要时咨询专业法律人士。
                  </p>
                </div>
              </div>
            </div>

            <aside className="lg:col-span-4">
              <div className="space-y-6 lg:sticky lg:top-28">
                <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
                  <div className="flex items-center gap-2">
                    <i className="ri-list-check-2 text-lg text-primary-600" />
                    <h2 className="font-heading text-base font-bold text-foreground-950">要点速览</h2>
                  </div>
                  <ul className="mt-4 space-y-3">
                    {article.keyPoints.map((point, index) => (
                      <li key={point} className="flex gap-3">
                        <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary-100 font-label text-xs font-semibold text-primary-700">
                          {index + 1}
                        </span>
                        <span className="text-sm leading-relaxed text-foreground-700">{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
                  <div className="mb-4 flex items-center gap-2">
                    <i className="ri-links-line text-lg text-secondary-700" />
                    <h2 className="font-heading text-base font-bold text-foreground-950">相关推荐</h2>
                  </div>
                  <div className="space-y-5">
                    {related.map((item) => (
                      <ArticleCard key={item.id} article={item} variant="minimal" />
                    ))}
                  </div>
                </div>

                <div className="rounded-lg bg-foreground-950 p-5 md:p-6">
                  <i className="ri-mail-send-line text-2xl text-accent-400" />
                  <h2 className="mt-3 font-heading text-base font-bold text-background-50">掌握更多细节？</h2>
                  <p className="mt-2 text-sm leading-relaxed text-background-300">
                    如果你有可查证的补充资料，欢迎通过投稿通道联系我们。
                  </p>
                  <Link
                    to="/contact"
                    className="mt-4 inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 font-label text-sm text-background-50 transition-colors hover:bg-primary-600"
                  >
                    我要投稿
                    <i className="ri-arrow-right-line" />
                  </Link>
                </div>
              </div>
            </aside>
          </div>
        </div>

        <RelatedArticles articles={related} />
      </article>
    </PageShell>
  );
}